import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Building2, ExternalLink, Image, X, Save, AlertCircle } from 'lucide-react';
import { Entity } from '../types';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';

interface EntitiesManagerProps {
  partnerId: string;
}

interface EntityFormData {
  name: string;
  logo_url: string;
  privacy_policy_url: string;
  comment: string;
}

const initialFormData: EntityFormData = {
  name: '',
  logo_url: '',
  privacy_policy_url: '',
  comment: ''
};

export const EntitiesManager: React.FC<EntitiesManagerProps> = ({ partnerId }) => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [formData, setFormData] = useState<EntityFormData>(initialFormData);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadEntities();
  }, [partnerId]);

  const loadEntities = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await DataService.getEntities(partnerId);
    if (fetchError) {
      setError(fetchError);
    } else {
      setEntities(data || []);
    }
    setLoading(false);
  };

  const handleOpenForm = (entity?: Entity) => {
    if (entity) {
      setEditingEntity(entity);
      setFormData({
        name: entity.name,
        logo_url: entity.logo_url || '',
        privacy_policy_url: entity.privacy_policy_url || '',
        comment: entity.comment || ''
      });
      setLogoPreview(entity.logo_url || null);
    } else {
      setEditingEntity(null);
      setFormData(initialFormData);
      setLogoPreview(null);
    }
    setLogoFile(null);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEntity(null);
    setFormData(initialFormData);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      // Clear URL when file is selected
      setFormData(prev => ({ ...prev, logo_url: '' }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, logo_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Entity name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingEntity) {
        const { data, error: updateError } = await DataService.updateEntity(
          editingEntity.id,
          {
            name: formData.name,
            logo_url: formData.logo_url,
            privacy_policy_url: formData.privacy_policy_url,
            comment: formData.comment
          },
          logoFile || undefined
        );
        
        if (updateError) {
          setError(updateError);
        } else if (data) {
          setEntities(prev => prev.map(e => e.id === data.id ? data : e));
          handleCloseForm();
        }
      } else {
        const { data, error: createError } = await DataService.createEntity(
          {
            name: formData.name,
            logo_url: formData.logo_url,
            privacy_policy_url: formData.privacy_policy_url,
            comment: formData.comment,
            partner_id: partnerId
          },
          logoFile || undefined
        );
        
        if (createError) {
          setError(createError);
        } else if (data) {
          setEntities(prev => [data, ...prev]);
          handleCloseForm();
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save entity');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error: deleteError } = await DataService.deleteEntity(id);
    if (deleteError) {
      setError(deleteError);
    } else {
      setEntities(prev => prev.filter(e => e.id !== id));
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Entities</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage sub-brands or business units for your organization
          </p>
        </div>
        <button
          onClick={() => handleOpenForm()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={18} />
          Add Entity
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="text-xs text-red-600 hover:text-red-800 mt-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Entities Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {entities.length === 0 ? (
          <div className="text-center py-12">
            <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No entities yet</h3>
            <p className="text-gray-500 mb-4">
              Create your first entity to organize your business units
            </p>
            <button
              onClick={() => handleOpenForm()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Plus size={18} />
              Add Entity
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Logo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Comment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Privacy Policy
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entities.map(entity => (
                  <tr key={entity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                        {entity.logo_url ? (
                          <img 
                            src={entity.logo_url} 
                            alt={entity.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Building2 size={24} className="text-gray-400" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{entity.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {entity.comment || <span className="text-gray-400 text-xs">No comment</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {entity.privacy_policy_url ? (
                        <a
                          href={entity.privacy_policy_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                        >
                          View Policy
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">Not set</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(entity.created).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenForm(entity)}
                          className="text-blue-600 hover:text-blue-800 transition duration-200"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(entity.id)}
                          className="text-red-600 hover:text-red-800 transition duration-200"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingEntity ? 'Edit Entity' : 'Create Entity'}
              </h3>
              <button
                onClick={handleCloseForm}
                className="p-1 text-gray-400 hover:text-gray-600 rounded transition"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Entity name"
                  required
                />
              </div>

              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo
                </label>
                
                {/* Logo URL Input */}
                <div className="mb-3">
                  <input
                    type="url"
                    value={formData.logo_url}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, logo_url: e.target.value }));
                      // Clear file when URL is entered
                      if (e.target.value) {
                        setLogoFile(null);
                        setLogoPreview(e.target.value);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter logo URL (e.g., https://example.com/logo.png)"
                    disabled={!!logoFile}
                  />
                  {logoFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      Clear uploaded file to use URL instead
                    </p>
                  )}
                </div>

                {/* OR divider */}
                <div className="relative my-3">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-white text-gray-500">OR upload image</span>
                  </div>
                </div>

                {/* File Upload and Preview */}
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border border-gray-200">
                    {logoPreview ? (
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <Image size={32} className="text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition"
                    >
                      <Image size={16} />
                      Choose Image
                    </label>
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="ml-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Recommended: Square image, PNG or JPG
                    </p>
                  </div>
                </div>
              </div>

              {/* Privacy Policy URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Privacy Policy URL
                </label>
                <input
                  type="url"
                  value={formData.privacy_policy_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, privacy_policy_url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/privacy"
                />
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comment / Description
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Optional description or notes about this entity"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingEntity ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Entity?</h3>
              <p className="text-sm text-gray-600 mb-6">
                This action cannot be undone. Are you sure you want to delete this entity?
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
